import { useState, useEffect } from 'react';
import { useInterviewStore, type Interview } from '../../../store/interviewStore';
import { useCandidateStore } from '../../../store/candidateStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from '../../../components/Dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator
} from '../../../components/DropdownMenu';
import { Plus, Search, Calendar, Video, Clock, CheckCircle2, MoreHorizontal, Trash2, MessageSquare, AlertCircle, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_FILTERS = [
  { id: 'all', label: 'All Interviews' },
  { id: 'scheduled', label: 'Upcoming' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const ROUND_TYPES = [
  'Initial HR Screening',
  'Technical Round 1 (Coding & DSA)',
  'Technical Round 2 (System Architecture)',
  'Managerial & Fitment Round',
  'Leadership & Cultural Round',
  'Final Offer Discussion',
];

export function InterviewScheduler() {
  const { interviews, isLoading, fetchInterviews, createInterview, updateInterview, deleteInterview } = useInterviewStore();
  const { candidates, fetchCandidates, createCandidate } = useCandidateStore();
  const { employees, fetchEmployees } = useEmployeeStore();

  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Modals state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);

  // Quick Add Candidate Mode inside modal
  const [isQuickAddCandidate, setIsQuickAddCandidate] = useState(false);
  const [quickCandidate, setQuickCandidate] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roleApplied: '',
  });

  // New Interview Form
  const [newInterview, setNewInterview] = useState({
    candidateId: '',
    interviewerId: '',
    type: 'Technical Round 1 (Coding & DSA)',
    scheduledTime: '',
    durationMinutes: 60,
  });

  // Feedback Form
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 4,
    recommendation: 'Hire',
    feedback: '',
    status: 'completed',
  });

  useEffect(() => {
    fetchInterviews();
    fetchCandidates();
    fetchEmployees();
  }, [fetchInterviews, fetchCandidates, fetchEmployees]);

  const filteredInterviews = interviews.filter(i => {
    const q = search.toLowerCase().trim();
    const candidateName = `${i.candidateFirstName || ''} ${i.candidateLastName || ''}`.toLowerCase();
    const interviewerName = `${i.interviewerFirstName || ''} ${i.interviewerLastName || ''}`.toLowerCase();
    const matchesSearch = 
      !q ||
      candidateName.includes(q) ||
      interviewerName.includes(q) ||
      i.type.toLowerCase().includes(q) ||
      (i.candidateRole || '').toLowerCase().includes(q);

    const matchesStatus = selectedStatus === 'all' || i.status.toLowerCase() === selectedStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const openScheduleModal = () => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    tomorrow.setHours(14, 0, 0, 0);

    setIsQuickAddCandidate(candidates.length === 0);
    setNewInterview({
      candidateId: candidates.length > 0 ? candidates[0].id : '',
      interviewerId: employees.length > 0 ? employees[0].id : '',
      type: 'Technical Round 1 (Coding & DSA)',
      scheduledTime: tomorrow.toISOString().slice(0, 16),
      durationMinutes: 60,
    });
    setQuickCandidate({
      firstName: '',
      lastName: '',
      email: '',
      roleApplied: '',
    });
    setIsScheduleModalOpen(true);
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let targetCandidateId = newInterview.candidateId;

    // If in quick add mode, create the candidate first
    if (isQuickAddCandidate) {
      if (!quickCandidate.firstName.trim() || !quickCandidate.email.trim() || !quickCandidate.roleApplied.trim()) {
        toast.error('Please fill in candidate first name, email, and role applied.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quickCandidate.email.trim())) {
        toast.error('Please enter a valid candidate email address.');
        return;
      }

      try {
        const created = await createCandidate({
          firstName: quickCandidate.firstName.trim(),
          lastName: quickCandidate.lastName.trim(),
          email: quickCandidate.email.trim().toLowerCase(),
          roleApplied: quickCandidate.roleApplied.trim(),
          status: 'interview',
        });
        targetCandidateId = created.id;
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to create candidate profile');
        return;
      }
    }

    if (!targetCandidateId) {
      toast.error('Please select or add a candidate');
      return;
    }
    if (!newInterview.scheduledTime) {
      toast.error('Please specify interview date & time');
      return;
    }

    try {
      const scheduled = await createInterview({
        candidateId: targetCandidateId,
        interviewerId: newInterview.interviewerId || undefined,
        type: newInterview.type,
        scheduledTime: new Date(newInterview.scheduledTime).toISOString(),
        durationMinutes: Number(newInterview.durationMinutes) || 60,
        status: 'scheduled',
      });
      toast.success('Interview scheduled successfully');
      setIsScheduleModalOpen(false);
      fetchInterviews();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to schedule interview');
    }
  };

  const openFeedbackModal = (interview: Interview) => {
    setSelectedInterview(interview);
    setFeedbackForm({
      rating: 4,
      recommendation: 'Hire',
      feedback: interview.feedback || '',
      status: 'completed',
    });
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInterview) return;

    try {
      const compiledFeedback = `[${feedbackForm.recommendation} - Rating: ${feedbackForm.rating}/5] ${feedbackForm.feedback}`.trim();
      await updateInterview(selectedInterview.id, {
        status: 'completed',
        feedback: compiledFeedback,
      });
      toast.success('Interview scorecard and feedback submitted');
      setIsFeedbackModalOpen(false);
      setSelectedInterview(null);
      fetchInterviews();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to submit feedback');
    }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await updateInterview(id, { status });
      toast.success(`Interview marked as ${status}`);
      fetchInterviews();
    } catch (err) {
      toast.error('Failed to update interview');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this interview record?')) {
      try {
        await deleteInterview(id);
        toast.success('Interview deleted');
      } catch (err) {
        toast.error('Failed to delete interview');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge variant="success" dot>Completed</Badge>;
      case 'scheduled':
        return <Badge variant="primary" dot>Upcoming</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="muted" dot>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">
            Interview Scheduler & Assessments
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Coordinate interview rounds, assign interviewers, and collect evaluation feedback.
          </p>
        </div>
        <Button onClick={openScheduleModal}>
          <Plus className="size-4" /> Schedule interview
        </Button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Calendar className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Upcoming Rounds</p>
              <h3 className="text-foreground font-display text-metric">
                {interviews.filter(i => i.status === 'scheduled').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
              <Clock className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Pending Feedback</p>
              <h3 className="text-foreground font-display text-metric">
                {interviews.filter(i => i.status === 'completed' && !i.feedback).length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Conducted</p>
              <h3 className="text-foreground font-display text-metric">
                {interviews.filter(i => i.status === 'completed').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-info/10 flex items-center justify-center text-info">
              <Video className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Rounds</p>
              <h3 className="text-foreground font-display text-metric">{interviews.length}</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card className="shadow-xs border-border">
        <CardHeader className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map(f => (
                <Button
                  key={f.id}
                  variant={selectedStatus === f.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedStatus(f.id)}
                  className="text-xs h-8"
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <div className="w-full sm:w-64">
              <Input
                leftIcon={<Search className="size-4 text-muted-foreground" />}
                placeholder="Search interview or candidate…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading interview schedule…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-secondary/40 text-muted-foreground font-medium text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Candidate</th>
                    <th className="px-6 py-3">Round / Assessment</th>
                    <th className="px-6 py-3">Interviewer</th>
                    <th className="px-6 py-3">Date & Time</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredInterviews.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                        No interviews scheduled matching current filters. Click "+ Schedule interview" to plan a round.
                      </td>
                    </tr>
                  ) : (
                    filteredInterviews.map((i) => (
                      <tr key={i.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-6 py-3.5">
                          <div className="font-medium text-foreground">
                            {i.candidateFirstName || 'Candidate'} {i.candidateLastName || ''}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {i.candidateRole || 'Applicant'}
                          </div>
                        </td>
                        <td className="px-6 py-3.5">
                          <span className="font-medium text-foreground">{i.type}</span>
                          <span className="block text-xs text-muted-foreground tabular-nums">
                            {i.durationMinutes} mins duration
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          {i.interviewerFirstName ? (
                            <span className="font-medium text-foreground">
                              {i.interviewerFirstName} {i.interviewerLastName}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">Unassigned</span>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-foreground tabular-nums text-sm">
                          {new Date(i.scheduledTime).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-6 py-3.5">
                          {getStatusBadge(i.status)}
                          {i.feedback && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[180px]" title={i.feedback}>
                              {i.feedback}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {i.status !== 'completed' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openFeedbackModal(i)}
                                className="text-xs h-7 gap-1"
                              >
                                <MessageSquare className="size-3.5" /> Feedback
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openFeedbackModal(i)}
                                className="text-xs h-7 gap-1 text-muted-foreground"
                              >
                                <FileText className="size-3.5" /> View Score
                              </Button>
                            )}

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm">
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleStatusUpdate(i.id, 'completed')}>
                                  <CheckCircle2 className="size-4 text-success mr-2" /> Mark as Completed
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(i.id, 'cancelled')}>
                                  <AlertCircle className="size-4 text-warning mr-2" /> Mark as Cancelled
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem destructive onClick={() => handleDelete(i.id)}>
                                  <Trash2 className="size-4 mr-2" /> Delete Interview
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Interview Modal */}
      <Dialog open={isScheduleModalOpen} onOpenChange={setIsScheduleModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Interview Round</DialogTitle>
            <DialogDescription>
              Select candidate, interviewer, assessment round, and meeting time.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleScheduleSubmit} className="space-y-4 pt-2">
            {/* Candidate Selector or Quick Add Toggle */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">Candidate *</label>
                <button
                  type="button"
                  onClick={() => setIsQuickAddCandidate(!isQuickAddCandidate)}
                  className="text-xs text-primary hover:underline flex items-center gap-1 font-medium"
                >
                  {isQuickAddCandidate ? 'Select from talent pool' : '+ Quick Add Candidate'}
                </button>
              </div>

              {!isQuickAddCandidate ? (
                <select
                  required
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={newInterview.candidateId}
                  onChange={(e) => setNewInterview({ ...newInterview, candidateId: e.target.value })}
                >
                  <option value="">Choose candidate...</option>
                  {candidates.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.firstName} {c.lastName} — ({c.roleApplied})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-secondary/30 border border-border rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">First Name *</label>
                      <Input
                        required
                        placeholder="John"
                        value={quickCandidate.firstName}
                        onChange={(e) => setQuickCandidate({ ...quickCandidate, firstName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Last Name</label>
                      <Input
                        placeholder="Doe"
                        value={quickCandidate.lastName}
                        onChange={(e) => setQuickCandidate({ ...quickCandidate, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Email Address *</label>
                      <Input
                        required
                        type="email"
                        placeholder="john.doe@example.com"
                        value={quickCandidate.email}
                        onChange={(e) => setQuickCandidate({ ...quickCandidate, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Role Applied For *</label>
                      <Input
                        required
                        placeholder="Software Engineer"
                        value={quickCandidate.roleApplied}
                        onChange={(e) => setQuickCandidate({ ...quickCandidate, roleApplied: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Assessment Round Type *</label>
              <select
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={newInterview.type}
                onChange={(e) => setNewInterview({ ...newInterview, type: e.target.value })}
              >
                {ROUND_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Assign Interviewer</label>
              <select
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={newInterview.interviewerId}
                onChange={(e) => setNewInterview({ ...newInterview, interviewerId: e.target.value })}
              >
                <option value="">Unassigned (Open for team)</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName} {emp.designation ? `(${emp.designation})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Date & Time *</label>
                <Input
                  required
                  type="datetime-local"
                  value={newInterview.scheduledTime}
                  onChange={(e) => setNewInterview({ ...newInterview, scheduledTime: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Duration</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={newInterview.durationMinutes}
                  onChange={(e) => setNewInterview({ ...newInterview, durationMinutes: Number(e.target.value) })}
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                </select>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsScheduleModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Schedule Interview
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feedback / Evaluation Modal */}
      <Dialog open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Interview Evaluation & Feedback</DialogTitle>
            <DialogDescription>
              Record evaluation scorecard for {selectedInterview?.candidateFirstName} {selectedInterview?.candidateLastName}.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleFeedbackSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Overall Rating (1 - 5)</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={feedbackForm.rating}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, rating: Number(e.target.value) })}
                >
                  <option value={5}>5 ★ - Outstanding</option>
                  <option value={4}>4 ★ - Strong Hire</option>
                  <option value={3}>3 ★ - Meets Bar</option>
                  <option value={2}>2 ★ - Below Bar</option>
                  <option value={1}>1 ★ - Strong No Hire</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Hiring Recommendation</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={feedbackForm.recommendation}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, recommendation: e.target.value })}
                >
                  <option value="Strong Hire">Strong Hire</option>
                  <option value="Hire">Hire</option>
                  <option value="Hold / Neutral">Hold / Neutral</option>
                  <option value="Reject">Reject</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Detailed Evaluation Notes & Key Strengths</label>
              <textarea
                className="w-full bg-field border border-input rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary h-28 resize-none"
                placeholder="Candidate demonstrated strong knowledge in data structures and API design. Communication was clear..."
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback: e.target.value })}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsFeedbackModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Submit Evaluation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
